import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { font, color } from '@metro-ui/tokens/js';
import { fontStack } from '../utils/fontStack';
import { responsiveProp, responsivePropTypes, } from '../utils/responsiveProp';
var headingColorMap = {
    primary: color.brand.primary.default,
    secondary: color.palette.grey.tint[40],
    inverted: color.inverted,
};
var textColorMap = {
    default: color.neutral,
    'brand-primary': color.brand.primary.default,
    muted: color.muted,
    disabled: color.disabled,
    danger: color.danger,
    success: color.success,
    inverted: color.inverted,
};
export var Heading = styled(function (_a) {
    var _b = _a.element, element = _b === void 0 ? 'h2' : _b, variant = _a.variant, colorVariant = _a.color, hasNoNativeMargin = _a.hasNoNativeMargin, align = _a.align, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["element", "variant", "color", "hasNoNativeMargin", "align", "className", "children"]);
    return React.createElement(element, __assign(__assign({}, htmlAttributes), { className: className }), children);
})(function (_a) {
    var _b = _a.variant, variant = _b === void 0 ? 'h2' : _b, _c = _a.color, colorVariant = _c === void 0 ? 'primary' : _c, hasNoNativeMargin = _a.hasNoNativeMargin, align = _a.align;
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    ", "\n    ", "\n  ", "\n\n  ", "\n  ", "\n  "], ["\n    ", "\n    ", "\n  ", "\n\n  ", "\n  ", "\n  "])), fontStack, responsiveProp(colorVariant, undefined, function (val) { return val && "color: " + headingColorMap[val] + ";"; }), responsiveProp(variant, undefined, function (val) {
        return val &&
            "\n    font-weight: " + font.heading[val].weight + ";\n    font-size: " + font.heading[val].size + ";\n    line-height: " + font.heading[val]['line-height'] + ";\n    ";
    }), hasNoNativeMargin && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      margin: 0;\n    "], ["\n      margin: 0;\n    "]))), responsiveProp(align, undefined, function (val) { return val && "text-align: " + val + ";"; }));
});
Heading.propTypes = {
    element: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    variant: responsivePropTypes(PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'intro', 'display'])),
    color: responsivePropTypes(PropTypes.oneOf(['primary', 'secondary', 'inverted'])),
    hasNoNativeMargin: PropTypes.bool,
    align: responsivePropTypes(PropTypes.oneOf(['left', 'center', 'right'])),
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
export var Text = styled(function (_a) {
    var colorVariant = _a.color, size = _a.size, align = _a.align, _b = _a.element, element = _b === void 0 ? 'p' : _b, hasNoNativeMargin = _a.hasNoNativeMargin, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["color", "size", "align", "element", "hasNoNativeMargin", "className", "children"]);
    return React.createElement(element, __assign(__assign({}, htmlAttributes), { className: className }), children);
})(function (_a) {
    var _b = _a.color, colorVariant = _b === void 0 ? 'default' : _b, _c = _a.size, size = _c === void 0 ? 'medium' : _c, hasNoNativeMargin = _a.hasNoNativeMargin, align = _a.align;
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", "\n    font-weight: ", ";\n    ", "\n\n    ", "\n  ", "\n  ", "\n  "], ["\n    ", "\n    font-weight: ", ";\n    ", "\n\n    ", "\n  ", "\n  ", "\n  "])), fontStack, font.weight.normal, responsiveProp(size, undefined, function (val) {
        return val &&
            "\n    font-size: " + font.text[val].size + ";\n    line-height: " + font.text[val]['line-height'] + ";\n    ";
    }), responsiveProp(colorVariant, undefined, function (val) { return val && "color: " + textColorMap[val] + ";"; }), hasNoNativeMargin && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      margin: 0;\n    "], ["\n      margin: 0;\n    "]))), responsiveProp(align, undefined, function (val) { return val && "text-align: " + val + ";"; }));
});
Text.propTypes = {
    color: responsivePropTypes(PropTypes.oneOf([
        'default',
        'brand-primary',
        'muted',
        'disabled',
        'danger',
        'success',
        'inverted',
    ])),
    size: responsivePropTypes(PropTypes.oneOf(['small', 'medium', 'large'])),
    element: PropTypes.oneOf(['p', 'span']),
    hasNoNativeMargin: PropTypes.bool,
    align: responsivePropTypes(PropTypes.oneOf(['left', 'center', 'right'])),
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
export var Strong = styled(function (_a) {
    var colorVariant = _a.color, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["color", "className", "children"]);
    return (React.createElement("strong", __assign({}, htmlAttributes, { className: className }), children));
})(function (_a) {
    var colorVariant = _a.color;
    return css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    ", "\n    font-weight: ", ";\n    ", "\n  "], ["\n    ", "\n    font-weight: ", ";\n    ", "\n  "])), fontStack, font.weight.heavy, responsiveProp(colorVariant, undefined, function (val) { return "color: " + (val ? textColorMap[val] : 'currentColor') + ";"; }));
});
Strong.propTypes = {
    color: responsivePropTypes(PropTypes.oneOf([
        'default',
        'brand-primary',
        'muted',
        'disabled',
        'danger',
        'inverted',
    ])),
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
export var Italic = styled(function (_a) {
    var colorVariant = _a.color, _b = _a.element, element = _b === void 0 ? 'em' : _b, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["color", "element", "className", "children"]);
    return React.createElement(element, __assign(__assign({}, htmlAttributes), { className: className }), children);
})(function (_a) {
    var colorVariant = _a.color;
    return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    ", "\n    font-style: italic;\n    ", "\n  "], ["\n    ", "\n    font-style: italic;\n    ", "\n  "])), fontStack, responsiveProp(colorVariant, undefined, function (val) { return "color: " + (val ? textColorMap[val] : 'currentColor') + ";"; }));
});
Italic.propTypes = {
    color: responsivePropTypes(PropTypes.oneOf([
        'default',
        'brand-primary',
        'muted',
        'disabled',
        'danger',
        'inverted',
    ])),
    element: PropTypes.oneOf(['em', 'i']),
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
export var Small = styled(function (_a) {
    var colorVariant = _a.color, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["color", "className", "children"]);
    return (React.createElement("small", __assign({}, htmlAttributes, { className: className }), children));
})(function (_a) {
    var colorVariant = _a.color;
    return css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    ", "\n    font-weight: ", ";\n    font-size: ", ";\n    line-height: ", ";\n    ", "\n  "], ["\n    ", "\n    font-weight: ", ";\n    font-size: ", ";\n    line-height: ", ";\n    ", "\n  "])), fontStack, font.weight.normal, font.text.small.size, font.text.small['line-height'], responsiveProp(colorVariant, undefined, function (val) { return "color: " + (val ? textColorMap[val] : 'currentColor') + ";"; }));
});
Small.propTypes = {
    color: responsivePropTypes(PropTypes.oneOf([
        'default',
        'brand-primary',
        'muted',
        'disabled',
        'danger',
        'inverted',
    ])),
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
export var Description = styled(function (_a) {
    var _b = _a.color, colorVariant = _b === void 0 ? 'muted' : _b, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["color", "className", "children"]);
    return (React.createElement(Text, __assign({}, htmlAttributes, { color: colorVariant, className: className }), children));
})(function () { return css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    font-style: italic;\n  "], ["\n    font-style: italic;\n  "]))); });
Description.propTypes = {
    color: responsivePropTypes(PropTypes.oneOf([
        'default',
        'brand-primary',
        'muted',
        'disabled',
        'danger',
        'inverted',
    ])),
    size: responsivePropTypes(PropTypes.oneOf(['small', 'medium', 'large'])),
    align: responsivePropTypes(PropTypes.oneOf(['left', 'center', 'right'])),
    element: PropTypes.oneOf(['p', 'span']),
    hasNoNativeMargin: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8;
//# sourceMappingURL=Typography.js.map